import { useRef, useState } from "react";
import { Loader2, Plus, Trash2, Upload } from "lucide-react";
import { useUploadWaTemplateSample } from "../../../lib/api";
import { inputClass } from "../primitives";
import { Chips, Field, focusRing } from "./ui";
import { CARD_BUTTON_KINDS, BUTTON_KINDS, FILE_ACCEPT, FILE_HINT, LIMITS, TYPES, fileKind, newButton, newCard } from "./templateFormat";

const dashedButton = `inline-flex h-10 cursor-pointer items-center gap-2 rounded-full border border-dashed border-[#9CA3AF] bg-white px-4 text-[13.5px] font-medium text-[#374151] transition-colors duration-150 hover:border-[#2667B5] hover:text-[#1F5596] disabled:cursor-not-allowed disabled:opacity-45 ${focusRing}`;

// The kinds of template.
export function TypePicker({ value, onChange }) {
  return (
    <div>
      <div className="mb-[9px] text-[13.5px] font-semibold text-[#374151]">Template type</div>
      <Chips label="Template type" value={value} onChange={onChange} options={TYPES.map(([v, l]) => [v, l])} />
    </div>
  );
}

// A sample file: uploaded, or a public link pasted in. Meta looks at it before approving.
export function MediaPicker({ kind, value, onChange, id, label = "Sample file" }) {
  const upload = useUploadWaTemplateSample();
  const input = useRef(null);
  const [error, setError] = useState("");

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setError("");
    try {
      const out = await upload.mutateAsync({ kind, file });
      onChange(out.url);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not upload the file. Try again in a moment.");
    }
  };

  return (
    <Field label={label} htmlFor={id} error={error}>
      <div className="flex gap-2">
        <input id={id} value={value} onChange={(e) => onChange(e.target.value)} placeholder="Upload a file, or paste its https:// link" className={inputClass} />
        <button
          type="button"
          onClick={() => input.current?.click()}
          disabled={upload.isPending}
          aria-label={`Upload ${label.toLowerCase()}`}
          className={`inline-flex h-12 shrink-0 cursor-pointer items-center gap-2 rounded-lg bg-[#111827] px-4 text-[14px] font-medium text-white transition-colors duration-150 hover:bg-[#1F2937] disabled:opacity-60 ${focusRing}`}
        >
          {upload.isPending ? <Loader2 className="animate-spin" size={15} aria-hidden="true" /> : <Upload size={15} aria-hidden="true" />}
          Upload
        </button>
        <input ref={input} type="file" accept={FILE_ACCEPT[kind]} onChange={pick} className="sr-only" tabIndex={-1} aria-hidden="true" data-testid={`${id}-file`} />
      </div>
      <p className="mb-0 mt-2 text-[12.5px] text-[#6B7280]">{FILE_HINT[kind]}</p>
    </Field>
  );
}

function CardButton({ index, button, onChange, onRemove }) {
  const set = (field) => (e) => onChange({ ...button, [field]: e.target.value });
  return (
    <div className="grid gap-3 rounded-lg bg-white p-3 ring-1 ring-[#E5E7EB] sm:grid-cols-[130px_1fr_auto]">
      <select aria-label={`Button ${index + 1} kind`} value={button.type} onChange={(e) => onChange({ ...newButton(e.target.value), text: button.text })} className={`${inputClass} !h-10`}>
        {CARD_BUTTON_KINDS.map((k) => (
          <option key={k} value={k}>
            {BUTTON_KINDS[k]}
          </option>
        ))}
      </select>
      <div className="grid gap-2 sm:grid-cols-2">
        <input aria-label={`Button ${index + 1} text`} value={button.text} onChange={set("text")} maxLength={LIMITS.buttonText} placeholder="Button text" className={`${inputClass} !h-10`} />
        {button.type === "URL" && <input aria-label={`Button ${index + 1} link`} value={button.url} onChange={set("url")} placeholder="https://example.com" className={`${inputClass} !h-10`} />}
        {button.type === "PHONE_NUMBER" && <input aria-label={`Button ${index + 1} phone number`} value={button.phone_number} onChange={set("phone_number")} placeholder="+919876543210" className={`${inputClass} !h-10`} />}
      </div>
      <button type="button" onClick={onRemove} aria-label={`Remove button ${index + 1}`} className={`flex h-10 w-10 cursor-pointer items-center justify-center rounded-lg text-[#6B7280] hover:bg-[#F3F4F6] ${focusRing}`}>
        <Trash2 size={15} aria-hidden="true" />
      </button>
    </div>
  );
}

// 2 to 10 cards. Every card has a file, an optional short text and 1 or 2 buttons; the cards all use the same buttons.
export function CardsEditor({ cards, onChange }) {
  const [kind, setKind] = useState(fileKind(cards[0]?.media_url) === "video" ? "video" : "image");
  const setCard = (i, patch) => onChange(cards.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  const addCard = () => onChange([...cards, { ...newCard(), buttons: (cards[0]?.buttons || newCard().buttons).map((b) => ({ ...newButton(b.type) })) }]);
  return (
    <div className="flex flex-col gap-4">
      <div>
        <div className="mb-[9px] text-[13.5px] font-semibold text-[#374151]">Cards show</div>
        <Chips label="Card file type" value={kind} onChange={setKind} options={[["image", "Pictures"], ["video", "Videos"]]} />
      </div>
      {cards.map((card, i) => (
        <section key={i} aria-label={`Card ${i + 1}`} className="rounded-xl bg-[#F9FAFB] p-4 ring-1 ring-[#E5E7EB]">
          <div className="mb-3 flex items-center justify-between">
            <h4 className="m-0 text-[13.5px] font-semibold text-[#374151]">Card {i + 1}</h4>
            {cards.length > LIMITS.minCards && (
              <button type="button" onClick={() => onChange(cards.filter((_, j) => j !== i))} aria-label={`Remove card ${i + 1}`} className={`flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg text-[#6B7280] hover:bg-white ${focusRing}`}>
                <Trash2 size={15} aria-hidden="true" />
              </button>
            )}
          </div>
          <div className="flex flex-col gap-4">
            <MediaPicker kind={kind} id={`tpl-card-${i}-file`} label={`Card ${i + 1} file`} value={card.media_url} onChange={(media_url) => setCard(i, { media_url })} />
            <Field label="Card text" htmlFor={`tpl-card-${i}-body`} optional count={card.body.length} max={LIMITS.cardBody}>
              <input id={`tpl-card-${i}-body`} value={card.body} onChange={(e) => setCard(i, { body: e.target.value })} maxLength={LIMITS.cardBody} className={inputClass} />
            </Field>
            <div className="flex flex-col gap-2">
              {card.buttons.map((b, j) => (
                <CardButton key={j} index={j} button={b} onChange={(next) => setCard(i, { buttons: card.buttons.map((x, k) => (k === j ? next : x)) })} onRemove={() => setCard(i, { buttons: card.buttons.filter((_, k) => k !== j) })} />
              ))}
              {card.buttons.length < LIMITS.cardButtons && (
                <button type="button" onClick={() => setCard(i, { buttons: [...card.buttons, newButton("QUICK_REPLY")] })} className={`${dashedButton} self-start`}>
                  <Plus size={14} aria-hidden="true" /> Add a button
                </button>
              )}
            </div>
          </div>
        </section>
      ))}
      {cards.length < LIMITS.cards && (
        <button type="button" onClick={addCard} className={`${dashedButton} self-start`}>
          <Plus size={14} aria-hidden="true" /> Add a card
          <span className="ml-1 text-[12.5px] tabular-nums text-[#9CA3AF]">
            {cards.length}/{LIMITS.cards}
          </span>
        </button>
      )}
    </div>
  );
}

// A code template is Meta's own fixed message. These are the few things that can change.
export function CodeOptions({ otp, onChange }) {
  const set = (patch) => onChange({ ...otp, ...patch });
  return (
    <div className="flex flex-col gap-5">
      <p className="m-0 rounded-xl bg-[#F9FAFB] px-4 py-3 text-[14px] leading-6 text-[#4B5563] ring-1 ring-[#E5E7EB]">
        Meta writes the message itself: "<strong>123456</strong> is your verification code." You choose only the options below.
      </p>
      <Field label="Button text" htmlFor="tpl-otp-button" count={otp.button_text.length} max={LIMITS.buttonText}>
        <input id="tpl-otp-button" value={otp.button_text} onChange={(e) => set({ button_text: e.target.value })} maxLength={LIMITS.buttonText} className={inputClass} />
      </Field>
      <Field label="Code expires after (minutes)" htmlFor="tpl-otp-expiry" optional>
        <input id="tpl-otp-expiry" type="number" min="1" max={LIMITS.expiry} value={otp.code_expiration_minutes} onChange={(e) => set({ code_expiration_minutes: e.target.value })} placeholder="For example, 10" className={inputClass} />
      </Field>
      <label className="flex cursor-pointer items-center gap-2 text-[14px] text-[#374151]">
        <input type="checkbox" checked={otp.security_recommendation} onChange={(e) => set({ security_recommendation: e.target.checked })} className="h-4 w-4 cursor-pointer accent-[#2667B5]" />
        Add "For your security, do not share this code."
      </label>
    </div>
  );
}
